/**
 * Personal Redirect Component
 *
 * Handle forward redirect from the legacy /personal/settings/:tab route
 * to the canonical /settings/:tab route.
 *
 * React Router v6's Navigate component doesn't interpolate URL parameters,
 * so we need this custom component that uses useParams to read dynamic segments
 * and construct the redirect URL.
 *
 * @see GW-422 for the original /settings -> /personal/settings redirect
 * @see GW-8263 for the direction flip: /settings is now canonical
 */

import type React from 'react';

import { Navigate, useParams } from 'react-router-dom';

import { useFeatureFlag } from '@/hooks/useFeatureFlag';

/**
 * Redirects /personal/settings/:tab to /settings/:tab
 */
export function PersonalSettingsTabRedirect(): React.JSX.Element {
  const { tab } = useParams<{ tab: string }>();
  return <Navigate to={`/settings/${tab}`} replace />;
}

/**
 * Redirects the bare /personal index route to its documented default tab
 * (missions), matching PersonalWorkspacePage's own DEFAULT_TAB/FALLBACK_TAB
 * logic — GW-8665: when missions_marketplace resolves disabled, `missions`
 * is unreachable (it self-redirects to sessions), so route straight to
 * `/personal/sessions` here too rather than bouncing through an extra hop.
 * While the flag is still loading, render nothing (no flash of the wrong tab).
 */
export function PersonalIndexRedirect(): React.JSX.Element | null {
  const missionsMarketplaceFlag = useFeatureFlag('missions_marketplace');

  if (!missionsMarketplaceFlag.hasLoaded) {
    return null;
  }

  return (
    <Navigate
      to={missionsMarketplaceFlag.isEnabled ? '/personal/missions' : '/personal/sessions'}
      replace
    />
  );
}
